import { useCallback, useEffect, useState } from 'react'
import { useAuthStore } from '@/store/authStore'
import { useTenantStore } from '@/store/tenantStore'

export interface CoachingPanelPreferences {
  lessonListCollapsed: boolean
  processListCollapsed: boolean
}
export type CoachingPanelPreferenceKey = keyof CoachingPanelPreferences

const STORAGE_PREFIX = 'lc:coaching:panel-collapse:v1'
const DEFAULT_PREFERENCES: CoachingPanelPreferences = {
  lessonListCollapsed: false,
  processListCollapsed: false,
}

function buildStorageKey(tenantId: string | null, userId: number | null): string | null {
  if (!tenantId || userId == null) return null
  return `${STORAGE_PREFIX}:${tenantId}:${userId}`
}

function readPreferences(storageKey: string | null): CoachingPanelPreferences {
  if (!storageKey) return { ...DEFAULT_PREFERENCES }

  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return { ...DEFAULT_PREFERENCES }
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed == null) return { ...DEFAULT_PREFERENCES }
    const stored = parsed as Record<string, unknown>
    return {
      lessonListCollapsed: typeof stored.lessonListCollapsed === 'boolean'
        ? stored.lessonListCollapsed
        : false,
      processListCollapsed: typeof stored.processListCollapsed === 'boolean'
        ? stored.processListCollapsed
        : false,
    }
  } catch {
    return { ...DEFAULT_PREFERENCES }
  }
}

function writePreferences(
  storageKey: string | null,
  preferences: CoachingPanelPreferences,
): void {
  if (!storageKey) return
  try {
    localStorage.setItem(storageKey, JSON.stringify(preferences))
  } catch {
    // 브라우저 저장소가 차단돼도 현재 화면의 접기 상태는 유지한다.
  }
}

export function usePersistedCoachingPanelCollapse(
  preferenceKey: CoachingPanelPreferenceKey,
) {
  const tenantId = useTenantStore((state) => state.tenantId)
  const userId = useAuthStore((state) => state.user?.id ?? null)
  const storageKey = buildStorageKey(tenantId, userId)
  const [collapsed, setCollapsed] = useState(
    () => readPreferences(storageKey)[preferenceKey],
  )

  useEffect(() => {
    setCollapsed(readPreferences(storageKey)[preferenceKey])
  }, [preferenceKey, storageKey])

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current
      const latest = readPreferences(storageKey)
      writePreferences(storageKey, { ...latest, [preferenceKey]: next })
      return next
    })
  }, [preferenceKey, storageKey])

  return { collapsed, toggle }
}
